import { defineComponent, ref } from 'vue-demi';
import { useToggle, onClickOutside } from '@vueuse/core';
import { OrIcon, OrSkeletonRect, OrSkeletonText, OrButtonV2, useResponsive } from '@onereach/ui-components';
import { u as useI18n } from './useI18n-9d7aeec3.js';
import _ from 'lodash';
import { S as SERVICES } from './services-8eb03020.js';
import { resolveComponent, openBlock, createElementBlock, createElementVNode, toDisplayString, createVNode, TransitionGroup, withCtx, Fragment, renderList, normalizeClass, withModifiers, createBlock, normalizeStyle, createCommentVNode, pushScopeId, popScopeId, renderSlot } from 'vue';
import { s as styleInject } from './style-inject.es-20d2f1f5.js';
import { s as script$4 } from './ServiceNavigationLogo-fb3a8f2b.js';
import { s as script$5 } from './ServiceNavigationContent-13848077.js';
import { s as script$7 } from './ServiceNavigationFooter-e6984176.js';
export { a as ServiceNavigationBreadcrumb } from './ServiceNavigationFooter-e6984176.js';
import { s as script$6 } from './UiProgressLinear-f7b5f03b.js';
import { s as script$3 } from './ServiceNavigationUtilitiesMenu-081f7818.js';
import { s as script$8 } from './ServiceNavigationNotifications-2bce5c10.js';
import { u as useUserServiceNavigationFavoritesServicesSettings, a as useServiceDiscoveryUiServices } from './useUserServiceNavigationFavoritesServicesSettings-1b3c3bab.js';
export { s as ServiceNavigationMenu } from './ServiceNavigationMenu-9bf50cbf.js';
import './constants/index.js';
import './EmojiPicker-87253538.js';
import '@onereach/emoji-picker-element';
import '@or-sdk/chat';
import '@or-sdk/notifications';
import '@or-sdk/discovery';
import '@or-sdk/settings';

function acronym(str) {
  if (_.isEmpty(str)) return '';
  const matches = str.match(/\b(\w)/g);
  return matches && matches.length ? matches.join('') : '';
}

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(31, h) + str.charCodeAt(i) | 0;
  }
  return h;
}
function intToColor(hash) {
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = hash >> i * 8 & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
}

function getOldServiceName(key) {
  const newToOld = _.invert(SERVICES);
  return key in newToOld ? newToOld[key] : key;
}
function prepareLocaleMessage(serviceKey, translations) {
  return _.mapValues(translations, t => ({
    services: {
      [_.kebabCase(serviceKey)]: t
    }
  }));
}
function prepareLocaleMessages(services) {
  return _.mapValues(_.groupBy(_.flatMap(services, s => _.map(_.entries(s.translations), t => ({
    key: s.serviceKey,
    locale: t[0],
    translation: t[1]
  }))), msg => msg.locale), msgs => ({
    'services': _.fromPairs(_.map(msgs, msg => [msg.key, msg.translation]))
  }));
}
function getServiceColor(service) {
  //TODO: return color if/when service has color property
  return intToColor(hashCode(`services.${getOldServiceName(_.isString(service) ? service : service.serviceKey)}`));
}
function getServiceLabel(service) {
  return `services.${_.isString(service) ? service : service.serviceKey}.label`;
}
function extractDropdownItems(services, activeLabel) {
  var _a, _b;
  let lastOrder = (_a = services === null || services === void 0 ? void 0 : services.reduce((prev, curr) => curr.order ? Math.max(prev, curr.order) : prev, 0)) !== null && _a !== void 0 ? _a : 0;
  return (_b = services === null || services === void 0 ? void 0 : services.map(item => {
    var _a, _b;
    return {
      label: getServiceLabel(item),
      active: activeLabel === getServiceLabel(item),
      url: (_a = item.url) !== null && _a !== void 0 ? _a : '/',
      id: item.serviceKey,
      order: (_b = item.order) !== null && _b !== void 0 ? _b : ++lastOrder
    };
  })) !== null && _b !== void 0 ? _b : [];
}
function extractServiceLocaleMessages(config, translations) {
  var _a;
  const serviceKey = (_a = SERVICES[config.service]) !== null && _a !== void 0 ? _a : config.service;
  return prepareLocaleMessage(serviceKey, translations);
}
function extractServiceLabel(config, services) {
  var _a;
  const serviceKey = (_a = SERVICES[config.service]) !== null && _a !== void 0 ? _a : _.kebabCase(config.service);
  if (services && !services.some(s => s.serviceKey === serviceKey)) {
    // eslint-disable-next-line max-len
    console.error(`Cannot recognize service '${serviceKey}'. Visit https://gitlab.internal.onereach.io/onereach/platform/ui-modules/service-navigation#add-logo-and-translations-to-projectconfig-in-packagejson-of-new-project`);
  }
  return getServiceLabel(serviceKey);
}
function getUtilitiesMenuItemsConfig(config) {
  let token = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return {
    ...config.UTILS_MENU_LIST,
    token
  };
}
function getHomePageUrl(config) {
  return config.REPORTING_URL;
}

var script$2 = defineComponent({
  name: 'ServiceNavigationDropdown',
  components: {
    OrIcon,
    OrSkeletonRect,
    OrSkeletonText
  },
  props: {
    dropdownItems: {
      type: Array,
      default: () => []
    },
    projectLabel: {
      type: String,
      default: ''
    },
    favoritesServices: {
      type: Array,
      default: () => []
    }
  },
  emits: ['toggleFavoritesServices'],
  setup() {
    const {
      translate
    } = useI18n();
    const [showMore, toggle] = useToggle();
    const contentRef = ref();
    const dropdownButtonRef = ref();
    const dropdownContainerRef = ref();
    onClickOutside(dropdownContainerRef, evt => {
      if (evt.composedPath().includes(dropdownButtonRef.value)) return;
      toggle(false);
    });
    return {
      showMore,
      toggle,
      translate,
      contentRef,
      dropdownButtonRef,
      dropdownContainerRef
    };
  },
  data() {
    return {
      leftWidth: '',
      nextKeyIndex: 0,
      itemKeys: {} // { label: index }
    };
  },

  computed: {
    skeletonsCount() {
      if (!this.contentRef) return 3;
      const width = this.contentRef.clientWidth + 50; // + grid-gap
      const count = Math.floor(width / (64 + 50)); // item width + grid-gap
      if (count <= 3) return count * 2; // 2 rows for small screens
      return count;
    },
    projectLabelTranslated() {
      return this.translate(this.projectLabel);
    },
    items() {
      if (!this.dropdownItems || this.dropdownItems.length === 0) {
        return [...Array(this.skeletonsCount).keys()].map(key => ({
          key
        }));
      }
      return (this.dropdownItems || []).map(item => {
        var _a;
        const color = getServiceColor(item.id);
        const translatedLabel = this.translate(item.label);
        if (!(item.id in this.itemKeys)) this.itemKeys[item.id] = this.nextKeyIndex++;
        const key = this.itemKeys[item.id];
        return {
          ...item,
          key,
          color,
          translatedLabel,
          acronym: acronym(translatedLabel),
          isFavorite: ((_a = this.favoritesServices) === null || _a === void 0 ? void 0 : _a.indexOf(item.id)) > -1
        };
      }).sort((i1, i2) => i1.order - i2.order).sort((i1, i2) => +i2.isFavorite - +i1.isFavorite);
    }
  },
  methods: {
    handleClick(item) {
      this.$emit('toggleFavoritesServices', item.id);
    }
  }
});

const _withScopeId = n => (pushScopeId("data-v-a947ae4c"), n = n(), popScopeId(), n);
const _hoisted_1$2 = {
  class: "dropdown",
  "data-test": "service-dropdown"
};
const _hoisted_2$1 = {
  class: "dr-app-label",
  "data-test": "service-dropdown-active-service-label"
};
const _hoisted_3$1 = {
  key: 0,
  ref: "dropdownContainerRef",
  class: "dr-container",
  "data-test": "service-dropdown-container"
};
const _hoisted_4$1 = /*#__PURE__*/_withScopeId(() => /*#__PURE__*/createElementVNode("div", {
  class: "favorites-container"
}, null, -1 /* HOISTED */));
const _hoisted_5$1 = {
  class: "dr-content-container"
};
const _hoisted_6 = {
  ref: "contentRef",
  class: "dr-content"
};
const _hoisted_7 = ["onClick"];
const _hoisted_8 = ["href"];
function render$2(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_or_icon = resolveComponent("or-icon");
  const _component_or_skeleton_rect = resolveComponent("or-skeleton-rect");
  const _component_or_skeleton_text = resolveComponent("or-skeleton-text");
  return openBlock(), createElementBlock("div", _hoisted_1$2, [createElementVNode("div", {
    ref: "dropdownButtonRef",
    "data-test": "service-dropdown-btn",
    class: "dr-btn",
    onClick: _cache[0] || (_cache[0] = $event => _ctx.toggle(!_ctx.showMore))
  }, [createElementVNode("div", _hoisted_2$1, toDisplayString(_ctx.projectLabelTranslated), 1 /* TEXT */), createVNode(_component_or_icon, {
    class: "toggle-indicator",
    icon: _ctx.showMore ? 'expand_less' : 'expand_more'
  }, null, 8 /* PROPS */, ["icon"])], 512 /* NEED_PATCH */), _ctx.showMore ? (openBlock(), createElementBlock("div", _hoisted_3$1, [_hoisted_4$1, createElementVNode("div", _hoisted_5$1, [createElementVNode("div", _hoisted_6, [createVNode(TransitionGroup, {
    tag: "span",
    name: "dropdown-items-group"
  }, {
    default: withCtx(() => [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.items, item => {
      return openBlock(), createElementBlock("div", {
        key: item.key,
        class: normalizeClass(['dr-item', {
          selected: item.isFavorite
        }]),
        "data-test": "service-dropdown-item"
      }, [item.id !== undefined ? (openBlock(), createElementBlock("div", {
        key: 0,
        class: "favorites-icon-wrapper",
        onClick: withModifiers($event => _ctx.handleClick(item), ["stop"])
      }, [createVNode(_component_or_icon, {
        icon: item.isFavorite ? 'star' : 'star_border'
      }, null, 8 /* PROPS */, ["icon"])], 8 /* PROPS */, _hoisted_7)) : (openBlock(), createBlock(_component_or_skeleton_rect, {
        key: 1,
        width: 64,
        height: 64,
        "is-animated": ""
      })), item.translatedLabel !== undefined ? (openBlock(), createElementBlock("a", {
        key: 2,
        href: item.url
      }, [createElementVNode("div", {
        class: "dr-item-img",
        "data-test": "service-dropdown-item-image",
        style: normalizeStyle(`background-color: ${item.color}`)
      }, toDisplayString(item.acronym), 5 /* TEXT, STYLE */), createElementVNode("p", {
        class: normalizeClass(['sn-link', {
          active: item.active
        }])
      }, toDisplayString(item.translatedLabel), 3 /* TEXT, CLASS */)], 8 /* PROPS */, _hoisted_8)) : (openBlock(), createBlock(_component_or_skeleton_text, {
        key: 3,
        class: "sn-link",
        size: "caption",
        "is-animated": ""
      }))], 2 /* CLASS */);
    }), 128 /* KEYED_FRAGMENT */))]),

    _: 1 /* STABLE */
  })], 512 /* NEED_PATCH */)])], 512 /* NEED_PATCH */)) : createCommentVNode("v-if", true)]);
}

var css_248z$3 = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-a947ae4c {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-a947ae4c {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n[data-theme=dark-mode] .dr-container[data-v-a947ae4c] {\n  background-color: var(--c-neutral-6-lighten-20);\n}\n[data-theme=dark-mode] .dr-container .dr-content-container > .dr-content a[data-v-a947ae4c] {\n  text-decoration: none;\n}\n[data-theme=dark-mode] .dr-container .dr-content-container > .dr-content a .dr-item-img[data-v-a947ae4c] {\n  color: var(--c-neutral-0);\n  background-color: var(--c-neutral-0);\n}\n[data-theme=dark-mode] .dr-container .dr-content-container > .dr-content a:hover .sn-link[data-v-a947ae4c] {\n  color: var(--c-neutral-0);\n}\n[data-theme=dark-mode] .dr-container .dr-content-container > .dr-content .sn-link[data-v-a947ae4c] {\n  color: var(--c-neutral-2);\n}\n[data-v-a947ae4c] .toggle-indicator {\n  font-size: var(--fs-1);\n}\n.dr-btn[data-v-a947ae4c] {\n  display: flex;\n  align-items: center;\n  height: 48px;\n  margin-left: var(--s-6);\n  font-size: var(--fs-2);\n  font-weight: bold;\n  color: var(--c-neutral-0);\n  cursor: pointer;\n}\n.dr-btn .dr-app-label[data-v-a947ae4c] {\n  margin-right: var(--s-3);\n  white-space: nowrap;\n}\n.dr-container[data-v-a947ae4c] {\n  position: absolute;\n  top: var(--s-7);\n  left: 0;\n  z-index: 51;\n  display: flex;\n  flex-direction: row;\n  width: 100vw;\n  overflow: auto;\n  background-color: var(--c-neutral-0);\n  box-shadow: 0 0 var(--s-4) 0 rgba(0, 0, 0, 0.2);\n  animation-name: slide-a947ae4c;\n  animation-duration: 0.2s;\n}\n.dr-container .favorites-container[data-v-a947ae4c] {\n  width: 148px;\n}\n.dr-container .dr-content-container[data-v-a947ae4c] {\n  display: flex;\n  flex: 1;\n  padding: var(--s-6);\n  margin-right: 148px;\n}\n.dr-container .dr-content-container .dr-content[data-v-a947ae4c] {\n  flex: 1;\n}\n.dr-container .dr-content-container .dr-content > span[data-v-a947ae4c] {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, var(--s-8));\n  grid-gap: 0 50px;\n}\n.dr-container .dr-content-container .dr-item[data-v-a947ae4c] {\n  position: relative;\n  display: flex;\n  flex-direction: column;\n  width: var(--s-8);\n  height: 86px;\n  margin-bottom: var(--s-6);\n  break-inside: avoid;\n}\n.dr-container .dr-content-container .dr-item:hover .favorites-icon-wrapper[data-v-a947ae4c] .or-icon {\n  display: block;\n}\n.dr-container .dr-content-container .dr-item[data-v-a947ae4c]:not(.selected) .or-icon {\n  display: none;\n}\n.dr-container .dr-content-container .dr-item .favorites-icon-wrapper[data-v-a947ae4c] {\n  position: absolute;\n  right: 0;\n  z-index: 1;\n  padding: var(--s-2);\n  cursor: pointer;\n}\n.dr-container .dr-content-container .dr-item .favorites-icon-wrapper[data-v-a947ae4c] .or-icon {\n  font-size: 16px;\n  color: var(--c-neutral-0);\n  cursor: pointer;\n}\n.dr-container .dr-content-container .dr-item a[data-v-a947ae4c] {\n  text-decoration: none;\n}\n.dr-container .dr-content-container .dr-item a:hover .sn-link[data-v-a947ae4c] {\n  color: var(--c-neutral-6);\n}\n.dr-container .dr-content-container .dr-item .dr-item-img[data-v-a947ae4c] {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--s-8);\n  height: var(--s-8);\n  font-size: 24px;\n  font-weight: bold;\n  color: var(--c-neutral-0);\n  background-color: var(--c-neutral-5);\n  border-radius: var(--s-2);\n  opacity: 0.7;\n}\n.dr-container .dr-content-container .dr-item .sn-link[data-v-a947ae4c] {\n  display: flex;\n  justify-content: center;\n  margin-top: var(--s-2);\n  font-size: 12px;\n  color: var(--c-neutral-5);\n  text-align: center;\n  white-space: nowrap;\n}\n.dr-container .dr-content-container .dr-item .active[data-v-a947ae4c] {\n  font-weight: bold;\n  color: var(--c-neutral-6);\n  pointer-events: none;\n  cursor: default;\n}\n@media all and (max-width: 768px) {\n.dr-btn[data-v-a947ae4c] {\n    align-items: center;\n    height: 100%;\n    margin: 0;\n}\n.dr-content-container[data-v-a947ae4c] {\n    top: var(--s-7);\n    right: 0;\n    left: 0;\n    margin-right: 0 !important;\n}\n.favorites-container[data-v-a947ae4c] {\n    display: none;\n}\n}\n.dropdown-items-group-move[data-v-a947ae4c],\n.dropdown-items-group-enter-active[data-v-a947ae4c],\n.dropdown-items-group-leave-active[data-v-a947ae4c] {\n  transition: all 0.5s ease;\n}\n@keyframes slide-a947ae4c {\nfrom {\n    min-height: 0;\n    max-height: 0;\n}\n}\n@keyframes slide-item-a947ae4c {\nfrom {\n    display: none;\n    opacity: 0;\n}\nto {\n    display: flex;\n    opacity: 1;\n}\n}";
styleInject(css_248z$3);

script$2.render = render$2;
script$2.__scopeId = "data-v-a947ae4c";
script$2.__file = "src/components/ServiceNavigationDropdown.vue";

var script$1 = defineComponent({
  name: 'ServiceNavigationDrawer',
  components: {
    OrButton: OrButtonV2
  },
  emits: ['open', 'close'],
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const [isOpen, toggle] = useToggle();
    const {
      isDesktop
    } = useResponsive();
    const close = () => {
      toggle(false);
      emit('close');
    };
    const open = () => {
      toggle(true);
      emit('open');
    };
    return {
      isDesktop,
      isOpen,
      open,
      close
    };
  },
  computed: {
    icons() {
      return this.isOpen ? 'close' : 'more_vert';
    },
    drawerClass() {
      return ['drawer-container', {
        'drawer-container--opened': !this.isDesktop && this.isOpen
      }];
    }
  }
});

const _hoisted_1$1 = {
  class: "content-container"
};
function render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_or_button = resolveComponent("or-button");
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(_ctx.drawerClass)
  }, [createElementVNode("div", _hoisted_1$1, [renderSlot(_ctx.$slots, "content")]), createVNode(_component_or_button, {
    class: "drawer-button",
    icon: _ctx.icons,
    color: "neutral-6",
    size: "small",
    onClick: _cache[0] || (_cache[0] = withModifiers($event => _ctx.isOpen ? _ctx.close() : _ctx.open(), ["prevent"]))
  }, null, 8 /* PROPS */, ["icon"])], 2 /* CLASS */);
}

var css_248z$2 = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-05e9340c {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-05e9340c {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.drawer-container[data-v-05e9340c] {\n  position: relative;\n  display: flex;\n  gap: var(--s-4);\n  background: var(--c-neutral-6);\n}\n.drawer-container .content-container[data-v-05e9340c] {\n  display: flex;\n  flex: 1;\n  flex-direction: row;\n  gap: var(--s-3);\n  align-items: center;\n  justify-content: end;\n}\n@media only screen and (max-width: 576px) {\n.drawer-container .content-container[data-v-05e9340c] {\n    display: none;\n    gap: var(--s-4);\n}\n.drawer-container--opened[data-v-05e9340c] {\n    position: absolute;\n    top: var(--s-3);\n    left: 0;\n    justify-content: end;\n    width: 100%;\n    padding: 0 var(--s-4);\n}\n.drawer-container--opened .content-container[data-v-05e9340c] {\n    display: flex;\n}\n}\n.drawer-container .drawer-button[data-v-05e9340c] {\n  display: none;\n}\n@media only screen and (max-width: 576px) {\n.drawer-container .drawer-button[data-v-05e9340c] {\n    display: flex;\n}\n}\n.drawer-container[data-v-05e9340c] .or-button-left-icon {\n  margin-right: 0;\n}\n.drawer-container .or-button[data-v-05e9340c] {\n  align-self: center;\n  padding: 0;\n}\n.drawer-container .or-button[data-v-05e9340c] .or-icon {\n  font-size: 24px;\n  color: var(--c-neutral-4);\n  cursor: pointer;\n  user-select: none;\n}\n@media (hover: hover) and (pointer: fine) {\n.drawer-container .or-button[data-v-05e9340c]:not(.drawer-container .or-button.is-loading):not(.drawer-container .or-button.is-disabled):not(.drawer-container .or-button.is-readonly):not(.drawer-container .or-button:disabled):not(.drawer-container .or-button.is-focused):not(.drawer-container .or-button:active):hover .or-icon {\n    color: var(--c-neutral-0);\n}\n}\n@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {\n.drawer-container .or-button[data-v-05e9340c]:not(.drawer-container .or-button.is-loading):not(.drawer-container .or-button.is-disabled):not(.drawer-container .or-button.is-readonly):not(.drawer-container .or-button:disabled):not(.drawer-container .or-button.is-focused):not(.drawer-container .or-button:active):hover .or-icon {\n    color: var(--c-neutral-0);\n}\n}";
styleInject(css_248z$2);

script$1.render = render$1;
script$1.__scopeId = "data-v-05e9340c";
script$1.__file = "src/components/ServiceNavigationDrawer.vue";

var script = defineComponent({
  components: {
    ServiceNavigationDrawer: script$1,
    ServiceNavigationUtilitiesMenu: script$3,
    ServiceNavigationLogo: script$4,
    ServiceNavigationContent: script$5,
    ServiceNavigationDropdown: script$2,
    UiProgressLinear: script$6,
    ServiceNavigationFooter: script$7,
    ServiceNavigationNotifications: script$8
  },
  props: {
    label: {
      type: String,
      default: null
    },
    showUtilsMenu: {
      type: Boolean,
      default: true
    },
    config: {
      type: Object,
      required: true
    },
    token: {
      type: String,
      required: false,
      default: null
    },
    accountId: {
      type: String,
      required: false,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    showServices: {
      type: Boolean,
      default: true
    },
    serviceTranslations: {
      type: Object,
      default: null
    },
    breadcrumbItems: {
      type: Array,
      default: () => []
    },
    menuItems: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const {
      initializeI18n,
      mergeLocaleMessages
    } = useI18n();
    const {
      isDesktop
    } = useResponsive();
    const {
      getUserFavoritesServices,
      toggleUserFavoritesServices
    } = useUserServiceNavigationFavoritesServicesSettings(() => props.token, {
      sdkUrl: props.config.SDK_API_URL
    });
    const {
      getUiServices
    } = useServiceDiscoveryUiServices(() => props.token, {
      discoveryUrl: props.config.SERVICE_DISCOVERY_API_URL
    }, props.config.FEATURE);
    return {
      initializeI18n,
      mergeLocaleMessages,
      isDesktop,
      getUserFavoritesServices,
      toggleUserFavoritesServices,
      getUiServices
    };
  },
  computed: {
    serviceItems() {
      return extractDropdownItems(this.getUiServices, this.projectLabel);
    },
    projectLabel() {
      return this.label || extractServiceLabel(this.config, this.getUiServices);
    },
    defaultLogoTarget() {
      return getHomePageUrl(this.config);
    },
    logoUrl() {
      return this.config.BRAND_LOGO_URL || null;
    },
    utilitiesMenuItemsConfig() {
      return getUtilitiesMenuItemsConfig(this.config, this.token);
    },
    service() {
      return this.config.service;
    }
  },
  watch: {
    getUiServices(val) {
      if (!(val === null || val === void 0 ? void 0 : val.length)) return;
      const localeMessages = prepareLocaleMessages(val);
      this.mergeLocaleMessages(localeMessages);
    },
    serviceTranslations: {
      handler(val) {
        if (val) {
          const localeMessages = extractServiceLocaleMessages(this.config, val);
          this.mergeLocaleMessages(localeMessages);
        }
      },
      immediate: true
    }
  },
  created() {
    this.initializeI18n();
  }
});

const _hoisted_1 = {
  class: "service-nav",
  "data-test": "service-navigation"
};
const _hoisted_2 = {
  class: "header"
};
const _hoisted_3 = {
  class: "services-container"
};
const _hoisted_4 = {
  class: "account",
  "data-test": "account-container"
};
const _hoisted_5 = {
  class: "loader",
  "data-test": "loader-container"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_service_navigation_logo = resolveComponent("service-navigation-logo");
  const _component_service_navigation_dropdown = resolveComponent("service-navigation-dropdown");
  const _component_service_navigation_content = resolveComponent("service-navigation-content");
  const _component_service_navigation_notifications = resolveComponent("service-navigation-notifications");
  const _component_service_navigation_utilities_menu = resolveComponent("service-navigation-utilities-menu");
  const _component_service_navigation_drawer = resolveComponent("service-navigation-drawer");
  const _component_service_navigation_footer = resolveComponent("service-navigation-footer");
  const _component_ui_progress_linear = resolveComponent("ui-progress-linear");
  return openBlock(), createElementBlock("header", _hoisted_1, [createElementVNode("div", _hoisted_2, [createElementVNode("div", _hoisted_3, [createVNode(_component_service_navigation_logo, {
    "logo-url": _ctx.logoUrl,
    "default-logo-target": _ctx.defaultLogoTarget,
    service: _ctx.service
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "logo")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["logo-url", "default-logo-target", "service"]), _ctx.showServices ? (openBlock(), createBlock(_component_service_navigation_dropdown, {
    key: 0,
    "dropdown-items": _ctx.serviceItems,
    "project-label": _ctx.projectLabel,
    "favorites-services": _ctx.getUserFavoritesServices,
    onToggleFavoritesServices: _ctx.toggleUserFavoritesServices
  }, null, 8 /* PROPS */, ["dropdown-items", "project-label", "favorites-services", "onToggleFavoritesServices"])) : createCommentVNode("v-if", true), _ctx.isDesktop ? (openBlock(), createBlock(_component_service_navigation_content, {
    key: 1,
    "menu-items": _ctx.menuItems
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "content")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["menu-items"])) : createCommentVNode("v-if", true)]), createVNode(_component_service_navigation_drawer, null, {
    content: withCtx(() => [_ctx.token && _ctx.config.NOTIFICATIONS_API_URL ? (openBlock(), createBlock(_component_service_navigation_notifications, {
      key: 0,
      config: _ctx.config,
      token: _ctx.token,
      "account-id": _ctx.accountId
    }, null, 8 /* PROPS */, ["config", "token", "account-id"])) : createCommentVNode("v-if", true), _ctx.showUtilsMenu ? (openBlock(), createBlock(_component_service_navigation_utilities_menu, {
      key: 1,
      config: _ctx.utilitiesMenuItemsConfig
    }, null, 8 /* PROPS */, ["config"])) : createCommentVNode("v-if", true), createElementVNode("div", _hoisted_4, [renderSlot(_ctx.$slots, "account")])]),
    _: 3 /* FORWARDED */
  })]), createVNode(_component_service_navigation_footer, {
    "breadcrumb-items": _ctx.breadcrumbItems,
    "menu-items": _ctx.menuItems
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "footer")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["breadcrumb-items", "menu-items"]), createElementVNode("div", _hoisted_5, [_ctx.loading ? (openBlock(), createBlock(_component_ui_progress_linear, {
    key: 0
  })) : createCommentVNode("v-if", true)])]);
}

var css_248z$1 = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-59a12196 {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-59a12196 {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.service-nav[data-v-59a12196] {\n  line-height: var(--lh-1);\n}\n.service-nav .header[data-v-59a12196] {\n  display: flex;\n  flex-shrink: 0;\n  height: 48px;\n  padding: 0 var(--s-6);\n  background: var(--c-neutral-6);\n}\n.service-nav .header .services-container[data-v-59a12196] {\n  display: flex;\n  flex: 1;\n}\n.service-nav .header .services-container .dropdown[data-v-59a12196] {\n  display: flex;\n  flex: 1;\n  justify-content: center;\n}\n.service-nav .header .account[data-v-59a12196] {\n  z-index: 45;\n}\n.service-nav .header .content[data-v-59a12196] {\n  width: 100%;\n}\n@media only screen and (max-width: 768px) {\n.service-nav .header[data-v-59a12196] {\n    justify-content: space-between;\n    padding: 0 var(--s-4);\n}\n}\n.service-nav .loader[data-v-59a12196] {\n  position: absolute;\n  top: 48px;\n  z-index: 40;\n  width: 100%;\n  height: 5px;\n}";
styleInject(css_248z$1);

var css_248z = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.dark emoji-picker,\n[data-theme=dark-mode] emoji-picker { /* stylelint-disable-line selector-type-no-unknown */\n  --background: var(--c-neutral-6-lighten-20);\n  --border-color: var(--c-neutral-5);\n  --indicator-color: var(--c-neutral-2-darken-5) ;\n}\n.dark .service-nav,\n[data-theme=dark-mode] .service-nav {\n  z-index: 10;\n  transform: translate3d(0, 0, 0);\n}\n.dark .service-nav .dr-container,\n[data-theme=dark-mode] .service-nav .dr-container {\n  background-color: var(--c-neutral-6-lighten-20) !important;\n  box-shadow: none;\n}\n.dark .service-nav .dr-container .dr-item a p,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a p {\n  color: var(--c-neutral-2) !important;\n}\n@media (hover: hover) and (pointer: fine) {\n.dark .service-nav .dr-container .dr-item a:not(.dark .service-nav .dr-container .dr-item a.is-loading,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-loading):not(.dark .service-nav .dr-container .dr-item a.is-disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-disabled):not(.dark .service-nav .dr-container .dr-item a.is-readonly,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-readonly):not(.dark .service-nav .dr-container .dr-item a:disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:disabled):not(.dark .service-nav .dr-container .dr-item a.is-focused,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-focused):not(.dark .service-nav .dr-container .dr-item a:active,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:active):hover p,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:not(.dark .service-nav .dr-container .dr-item a.is-loading,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-loading):not(.dark .service-nav .dr-container .dr-item a.is-disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-disabled):not(.dark .service-nav .dr-container .dr-item a.is-readonly,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-readonly):not(.dark .service-nav .dr-container .dr-item a:disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:disabled):not(.dark .service-nav .dr-container .dr-item a.is-focused,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-focused):not(.dark .service-nav .dr-container .dr-item a:active,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:active):hover p {\n    color: var(--c-neutral-0) !important;\n}\n}\n@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {\n.dark .service-nav .dr-container .dr-item a:not(.dark .service-nav .dr-container .dr-item a.is-loading,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-loading):not(.dark .service-nav .dr-container .dr-item a.is-disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-disabled):not(.dark .service-nav .dr-container .dr-item a.is-readonly,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-readonly):not(.dark .service-nav .dr-container .dr-item a:disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:disabled):not(.dark .service-nav .dr-container .dr-item a.is-focused,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-focused):not(.dark .service-nav .dr-container .dr-item a:active,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:active):hover p,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:not(.dark .service-nav .dr-container .dr-item a.is-loading,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-loading):not(.dark .service-nav .dr-container .dr-item a.is-disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-disabled):not(.dark .service-nav .dr-container .dr-item a.is-readonly,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-readonly):not(.dark .service-nav .dr-container .dr-item a:disabled,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:disabled):not(.dark .service-nav .dr-container .dr-item a.is-focused,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a.is-focused):not(.dark .service-nav .dr-container .dr-item a:active,\n[data-theme=dark-mode] .service-nav .dr-container .dr-item a:active):hover p {\n    color: var(--c-neutral-0) !important;\n}\n}\n.dark .service-nav .footer,\n[data-theme=dark-mode] .service-nav .footer {\n  border: none;\n  background-color: var(--c-neutral-6-lighten-20) !important;\n  box-shadow: none;\n}\n.dark .service-nav .footer .menu .service-navigation-menu-items a,\n[data-theme=dark-mode] .service-nav .footer .menu .service-navigation-menu-items a {\n  color: var(--c-neutral-2);\n}\n.dark .service-nav .footer .menu .service-navigation-menu-items a.router-link-exact-active,\n[data-theme=dark-mode] .service-nav .footer .menu .service-navigation-menu-items a.router-link-exact-active {\n  color: var(--c-neutral-6);\n  background-color: var(--c-neutral-0);\n}\n.dark .service-nav .footer .menu .more-items-container,\n[data-theme=dark-mode] .service-nav .footer .menu .more-items-container {\n  background-color: var(--c-neutral-6-lighten-20) !important;\n  box-shadow: none;\n  border: none;\n}\n.dark .service-nav .footer .menu .more-items-container a,\n[data-theme=dark-mode] .service-nav .footer .menu .more-items-container a {\n  color: var(--c-neutral-2);\n}\n.dark .service-nav .footer .menu .more-items-trigger,\n[data-theme=dark-mode] .service-nav .footer .menu .more-items-trigger {\n  background-color: var(--c-neutral-6-lighten-20) !important;\n  box-shadow: none;\n}\n.dark .service-nav .footer .menu .more-items-trigger svg,\n[data-theme=dark-mode] .service-nav .footer .menu .more-items-trigger svg {\n  fill: var(--c-neutral-2);\n}\n.dark .service-nav .footer .breadcrumbs span,\n.dark .service-nav .footer .breadcrumbs-mobile span,\n[data-theme=dark-mode] .service-nav .footer .breadcrumbs span,\n[data-theme=dark-mode] .service-nav .footer .breadcrumbs-mobile span {\n  color: var(--c-neutral-2);\n}\n.dark .service-nav .footer .breadcrumbs .more-items-container,\n.dark .service-nav .footer .breadcrumbs-mobile .more-items-container,\n[data-theme=dark-mode] .service-nav .footer .breadcrumbs .more-items-container,\n[data-theme=dark-mode] .service-nav .footer .breadcrumbs-mobile .more-items-container {\n  border: none;\n  background-color: var(--c-neutral-6-lighten-20) !important;\n  box-shadow: none;\n}\n.dark .service-nav .footer .breadcrumbs .breadcrumb-item:last-child .sn-link,\n.dark .service-nav .footer .breadcrumbs-mobile .breadcrumb-item:last-child .sn-link,\n[data-theme=dark-mode] .service-nav .footer .breadcrumbs .breadcrumb-item:last-child .sn-link,\n[data-theme=dark-mode] .service-nav .footer .breadcrumbs-mobile .breadcrumb-item:last-child .sn-link {\n  color: var(--c-neutral-0);\n}\n.dark .dropdown-container .content,\n[data-theme=dark-mode] .dropdown-container .content {\n  background-color: var(--c-neutral-6-lighten-20) !important;\n  box-shadow: none;\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-59a12196";
script.__file = "src/components/ServiceNavigationHeader.vue";

export { script as ServiceNavigationHeader };
